/**
 * Batch E commit E2 — SupervisorCallDetailPage.
 *
 * Drill-down detail route for a single active call on the mobile flow.
 * Mirrors the listen / whisper / barge actions that `CallMonitor`
 * renders on desktop. The actions hit the same REST endpoints
 * (`POST /supervisor/{action}/:callId`) the desktop view does —
 * that path is authoritative (idempotency keys + audit logging +
 * per-action permission gating).
 */
import { useState, type JSX } from 'react'
import { useNavigate, useParams } from 'react-router'
import { Button } from 'primereact/button'
import { useAuthStore } from '../../store/authStore.js'
import { useUiStore } from '../../store/uiStore.js'
import { useSupervisorStore } from '../../store/supervisorStore.js'

type Action = 'listen' | 'whisper' | 'barge'

function formatDuration(sec: number): string {
  const m = Math.floor(sec / 60)
  const s = String(sec % 60).padStart(2, '0')
  return `${m}:${s}`
}

interface Props {
  /** Override the REST base — tests inject a mock fetch and ignore it. */
  apiBaseUrl?: string
}

export default function SupervisorCallDetailPage(props: Props = {}): JSX.Element {
  const apiBaseUrl = props.apiBaseUrl ?? '/api'
  const { callId } = useParams<{ callId: string }>()
  const navigate = useNavigate()
  const call = useSupervisorStore((s) =>
    callId ? s.liveCalls.find((c) => c.callId === callId) ?? null : null,
  )
  const csrfToken = useAuthStore((s) => s.csrfToken)
  const addNotification = useUiStore((s) => s.addNotification)
  const [pending, setPending] = useState<Action | null>(null)

  async function invokeAction(action: Action): Promise<void> {
    if (!callId) return
    setPending(action)
    try {
      const res = await fetch(`${apiBaseUrl}/supervisor/${action}/${callId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
        },
        credentials: 'include',
        body: '{}',
      })
      if (!res.ok) {
        addNotification(`Supervisor ${action} failed (${res.status})`, 'error')
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Unknown error'
      addNotification(`Supervisor ${action} failed: ${msg}`, 'error')
    } finally {
      setPending(null)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-2">
      <div className="flex items-center gap-2">
        <Button
          icon="pi pi-arrow-left"
          text
          aria-label="Back to supervisor"
          onClick={() => navigate('/supervisor')}
        />
        <h1 className="text-sm font-semibold text-gray-800">Call detail</h1>
      </div>

      {!call ? (
        <div className="rounded border border-gray-200 bg-white p-4 text-sm text-gray-500">
          Call not found. It may have ended — return to the supervisor
          dashboard for the current active-call list.
        </div>
      ) : (
        <section className="rounded border border-gray-200 bg-white p-4 shadow-sm">
          <header className="mb-3">
            <div className="text-base font-semibold">{call.callerCLI}</div>
            <div className="text-xs text-gray-500">Agent: {call.agentName}</div>
          </header>
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-sm">
            <dt className="text-gray-500">Duration</dt>
            <dd>{formatDuration(call.durationSec)}</dd>
            <dt className="text-gray-500">Channel</dt>
            <dd className="font-mono text-xs">{call.channelId}</dd>
            {call.queueId && (
              <>
                <dt className="text-gray-500">Queue</dt>
                <dd className="font-mono text-xs">{call.queueId}</dd>
              </>
            )}
          </dl>
          <div className="mt-4 flex gap-2">
            <Button
              icon="pi pi-eye"
              label="Listen"
              disabled={pending !== null}
              onClick={() => void invokeAction('listen')}
            />
            <Button
              icon="pi pi-megaphone"
              label="Whisper"
              disabled={pending !== null}
              onClick={() => void invokeAction('whisper')}
            />
            <Button
              icon="pi pi-users"
              label="Barge"
              severity="warning"
              disabled={pending !== null}
              onClick={() => void invokeAction('barge')}
            />
          </div>
        </section>
      )}
    </div>
  )
}
